import { useState, useEffect } from 'react';

/**
 * useMobileDetect - 2025 Device Detection Hook
 *
 * Detects mobile devices, screen size breakpoints, and device capabilities
 * for adaptive UI rendering.
 *
 * Features:
 * - Real-time viewport tracking
 * - Touch capability detection
 * - iOS/Android differentiation
 * - Tablet detection
 * - Safe area inset support
 */
const useMobileDetect = () => {
  const [deviceInfo, setDeviceInfo] = useState(() => getDeviceInfo());

  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };

    // Listen for resize and orientation changes
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
};

/**
 * Get comprehensive device information
 */
function getDeviceInfo() {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const height = typeof window !== 'undefined' ? window.innerHeight : 768;

  // Platform detection
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isMobileUA = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);

  // Screen size breakpoints (matches Tailwind defaults)
  const isSmallScreen = width < 640;   // sm
  const isMediumScreen = width < 768;  // md
  const isLargeScreen = width >= 1024; // lg
  const isXLScreen = width >= 1280;    // xl

  // Device type determination - More aggressive mobile detection for 2025 devices
  // Most phones are < 768px wide in portrait, and have touch support
  const isMobile = isMobileUA || (isMediumScreen && hasTouchSupport());
  const isTablet = !isMobile && width < 1024 && hasTouchSupport();
  const isDesktop = !isMobile && !isTablet;

  // Orientation
  const isPortrait = height > width;
  const isLandscape = width > height;

  // Specific device detection
  const isIPhone = isIOS && /iphone/.test(userAgent);
  const isIPad = isIOS && /ipad/.test(userAgent);
  const hasNotch = isIPhone && (
    // iPhone X and later (notched devices)
    (width === 375 && height === 812) || // iPhone X, XS, 11 Pro, 12 mini, 13 mini
    (width === 414 && height === 896) || // iPhone XR, XS Max, 11, 11 Pro Max
    (width === 390 && height === 844) || // iPhone 12, 12 Pro, 13, 13 Pro, 14
    (width === 428 && height === 926) || // iPhone 12 Pro Max, 13 Pro Max, 14 Plus
    (width === 393 && height === 852) || // iPhone 14 Pro, 15, 15 Pro
    (width === 430 && height === 932) || // iPhone 14 Pro Max, 15 Plus, 15 Pro Max
    // Landscape equivalents
    (height === 375 && width === 812) ||
    (height === 414 && width === 896) ||
    (height === 390 && width === 844) ||
    (height === 428 && width === 926) ||
    (height === 393 && width === 852) ||
    (height === 430 && width === 932)
  );

  // Dynamic Island detection (iPhone 14 Pro and later)
  const hasDynamicIsland = isIPhone && (
    (width === 393 && height === 852) ||
    (width === 430 && height === 932) ||
    (height === 393 && width === 852) ||
    (height === 430 && width === 932)
  );

  return {
    // Core detection
    isMobile,
    isTablet,
    isDesktop,

    // Platform
    isIOS,
    isAndroid,
    isIPhone,
    isIPad,

    // Screen size
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    isXLScreen,
    width,
    height,

    // Orientation
    isPortrait,
    isLandscape,

    // Device features
    hasNotch,
    hasDynamicIsland,
    hasTouch: hasTouchSupport(),

    // Breakpoint helpers
    breakpoint: getBreakpoint(width),

    // Safe area helper (CSS env() values)
    safeAreaInsets: {
      top: hasNotch || hasDynamicIsland ? 'env(safe-area-inset-top, 44px)' : '0px',
      bottom: hasNotch ? 'env(safe-area-inset-bottom, 34px)' : '0px',
      left: 'env(safe-area-inset-left, 0px)',
      right: 'env(safe-area-inset-right, 0px)',
    },
  };
}

/**
 * Check for touch support
 */
function hasTouchSupport() {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - msMaxTouchPoints for older IE
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Get named breakpoint from width
 */
function getBreakpoint(width) {
  if (width < 640) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1280) return 'lg';
  return 'xl';
}

export default useMobileDetect;
